<script setup lang="ts">
import { useDebounce } from '@vueuse/core'
import { LucideSearch, LucideNewspaper, LucideCircleX } from 'lucide-vue-next'
import { FeaturePagination } from '~/components/feature/pagination'
import {
  EntityInsuranceItem,
  EntityInsuranceSkeleton,
  type IInsuranceCompanyCard,
  type IInsuranceQueryParams
} from '~/components/entity/insurance'
import type { IResponseData, IMultiselectOption } from '~/types'
import type { IInsuranceTypes } from '~/components/widget/select-insurance-type'
import type { SearchResponse } from 'meilisearch'

const config = useRuntimeConfig()
const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()

const props = withDefaults(
  defineProps<{
    queryParams?: IInsuranceQueryParams
  }>(),
  {
    queryParams: () => ({
      per_page: '10',
      q: ''
    })
  }
)

const hitsPerPage = Number(route.query.per_page || props.queryParams.per_page || 10)
const currentPage = ref(Number(route.query.page) || 1)
const searchQueryString = ref(String(route.query.q || ''))
const debouncedQuery = useDebounce(searchQueryString, 500)

const { data: types } = await useApi<IResponseData<IInsuranceTypes[]>>(() => '/insurance_types', {
  key: 'insurance-types-meili-search'
})

const forWhomOptions = computed(() => [
  {
    id: 1,
    label: t('widget.insurances.list_meili_search.for_whom_options.private_person'),
    value: 'private_person'
  },
  {
    id: 2,
    label: t('widget.insurances.list_meili_search.for_whom_options.business'),
    value: 'business'
  },
  {
    id: 3,
    label: t('widget.insurances.list_meili_search.for_whom_options.mandatory'),
    value: 'mandatory'
  }
])

const typesOptions = computed<IMultiselectOption[]>(
  () =>
    types.value?.data.map((item) => ({
      id: item.id,
      label: item.title,
      value: item.slug
    })) ?? []
)

const selectedForWhom = ref<typeof forWhomOptions>([])

const getInitialSelectedTypes = (): IMultiselectOption[] => {
  const lastSegment = route.path.split('/').filter(Boolean).pop()
  if (!lastSegment || lastSegment === 'insurances') return []
  const match = typesOptions.value.find((item) => item.value === lastSegment)
  return match ? [match] : []
}
const selectedTypes = ref<IMultiselectOption[]>(getInitialSelectedTypes())

const activeFilter = computed(() => {
  const allFilters: string[] = []

  if (selectedForWhom.value?.length) {
    allFilters.push(...selectedForWhom.value.map((o) => `for_whom.${o.value} = true`))
  }

  if (selectedTypes.value?.length) {
    allFilters.push(...selectedTypes.value.map((o) => `types.${o.id} EXISTS`))
  }

  return allFilters.length ? [`(${allFilters.join(' OR ')})`] : undefined
})

// Compute search key for tracking changes
const searchKey = computed(() =>
  [
    'insurances-search',
    debouncedQuery.value,
    currentPage.value,
    selectedForWhom.value.map((f) => f.value).join(','),
    selectedTypes.value.map((t) => t.id).join(',')
  ].join('-')
)

const isInitializing = ref(true)

const fetchInsurances = async () => {
  const { search } = useMeiliIndexSearch(`${config.public.meiliEnvironment}_insurance_companies`)

  try {
    return await search(debouncedQuery.value, {
      hitsPerPage,
      page: currentPage.value,
      sort: ['weight:desc', 'highlight:desc', 'updated_at:desc'],
      attributesToSearchOn: [
        'title.ky',
        'title.ru',
        'official_title.ky',
        'official_title.ru',
        'description.ky',
        'description.ru'
      ],
      attributesToHighlight: ['title', 'official_title'],
      ...(activeFilter.value ? { filter: activeFilter.value } : {})
    })
  } catch (e) {
    console.error('MeiliSearch fetch error:', e)
    return null
  }
}

// Use useAsyncData for SSR support
const {
  data: result,
  pending: isSearching,
  error: searchError
} = await useAsyncData<SearchResponse<IInsuranceCompanyCard> | null>(
  `insurances-search-${route.path}`,
  fetchInsurances,
  {
    server: true,
    lazy: true,
    watch: false,
    default: () => null
  }
)

watch(searchKey, async () => {
  if (isInitializing.value) return
  result.value = await fetchInsurances()
})

const hasError = computed(() => !!searchError.value)

const typedHits = computed(() => {
  return (result.value?.hits || []).map((hit) => ({
    id: hit.id,
    title: hit._formatted?.title[locale.value] || hit.title[locale.value],
    official_title: hit.official_title[locale.value],
    email: hit.email,
    url: hit.url,
    logo: hit.logo,
    website_url: hit.website_url,
    highlight: hit.highlight,
    ratings: hit.ratings ? hit.ratings.average_rate : null
  }))
})

onMounted(() => {
  isInitializing.value = false
})

watch(
  () => route.path,
  (path) => {
    const lastSegment = path.split('/').filter(Boolean).pop()
    if (!lastSegment || lastSegment === 'insurances') return

    const match = typesOptions.value.find((item) => item.value === lastSegment)
    if (match) {
      selectedTypes.value = [match]
    } else {
      selectedTypes.value = []
    }
  },
)

watch(debouncedQuery, () => {
  if (isInitializing.value) return
  currentPage.value = 1
  router.replace({
    path: route.path,
    query: { ...route.query, q: debouncedQuery.value, page: undefined }
  })
})

watch([selectedForWhom, selectedTypes], () => {
  if (isInitializing.value) return
  if (currentPage.value !== 1) currentPage.value = 1
})

const changePage = (page: number) => {
  router.replace({ path: route.path, query: { ...route.query, q: searchQueryString.value, page } })
  currentPage.value = page
}
</script>

<template>
  <div class="space-y-5">
    <div class="relative">
      <Input
        v-model="searchQueryString"
        :placeholder="$t('common.search_placeholder')"
        class="h-[52px] pl-14 pr-14"
      />
      <LucideSearch class="absolute left-4 top-3.5 size-6 text-gray-600" />
      <Button
        v-if="searchQueryString"
        size="icon"
        variant="link"
        class="absolute right-4 top-3.5 size-6 text-red-400 hover:text-red-400"
        @click="searchQueryString = ''"
      >
        <LucideCircleX />
      </Button>
    </div>

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <MultiSelect
        v-model="selectedTypes"
        :options="typesOptions"
        :placeholder="$t('widget.insurances.list_meili_search.insurance_type')"
        class="multiselect_height_52"
      />
      <MultiSelect
        v-model="selectedForWhom"
        class="multiselect_height_52"
        :options="forWhomOptions"
        :placeholder="$t('widget.insurances.list_meili_search.for_whom')"
      />
    </div>

    <section v-if="!isSearching && !hasError && result">
      <div v-if="result.hits?.length" class="CardsList">
        <EntityInsuranceItem
          v-for="item in typedHits"
          :key="item.id"
          :item="item"
          :hide-organization-details="false"
        />

        <div class="mt-7 flex justify-end">
          <FeaturePagination
            :meta="{
              current_page: result.page || 1,
              total: result.totalHits || 0,
              per_page: result.hitsPerPage || 0,
              last_page: result.totalPages || 0
            }"
            :links="{
              prev:
                currentPage > 1
                  ? `?q=${searchQueryString}&page=${result.page ? result.page - 1 : 1}`
                  : null,
              next:
                currentPage < (result.totalPages || 1)
                  ? `?q=${searchQueryString}&page=${result.page ? result.page + 1 : 1}`
                  : null
            }"
            @change="changePage"
          />
        </div>
      </div>

      <Alert v-else variant="blue">
        <LucideNewspaper />
        <AlertDescription>{{ $t('common.nothing_found') }}</AlertDescription>
      </Alert>
    </section>

    <section v-else-if="isSearching">
      <EntityInsuranceSkeleton :count="hitsPerPage" :hide-organization-details="false" />
    </section>

    <section v-else-if="hasError">
      <Alert variant="yellow">
        <AlertTitle>{{ $t('common.error_loading_title') }}</AlertTitle>
        <AlertDescription>{{ $t('common.error_loading_description') }}</AlertDescription>
      </Alert>
    </section>
  </div>
</template>
